import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import {libraryShape} from 'components/Shapes';
import LibraryTableHeader from 'components/pure/admin/LibraryTableHeader';
import LibraryTableRow from 'components/pure/admin/LibraryTableRow';

const columns = ['Library Name', 'Created By', '# Calls', 'Last Modified', 'Sharing', 'Manage'];
const sortableColumns = ['Library Name', 'Created By', '# Calls', 'Last Modified', 'Sharing'];

export default class LibraryTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      sortColumn: columns[0],
      sortDirection: 'up',
    };

    this.sorter = new LibrarySorter(this.state.sortColumn, this.state.sortDirection);
    this.handleSort = this.handleSort.bind(this);
  }

  renderTable(libraries) {
    const {sortColumn, sortDirection} = this.state;

    return (
      <table className="user-list-table user-list">
        <LibraryTableHeader
          {...{columns, sortableColumns, sortColumn, sortDirection}}
          onSort={this.handleSort}
        />
        <tbody>{this.renderTableRows(libraries)}</tbody>
      </table>
    );
  }

  handleSort(columnName) {
    if (this.state.sortColumn === columnName) {
      const newDirection = this.reverseDirection();

      this.sorter.setSortDirection(newDirection);
      this.setState({
        sortDirection: newDirection,
      });
    } else {
      this.sorter.setSortColumn(columnName);
      this.sorter.setSortDirection('up');
      this.setState({
        sortColumn: columnName,
        sortDirection: 'up',
      });
    }
  }

  reverseDirection() {
    if (this.state.sortDirection === 'up') {
      return 'down';
    }

    return 'up';
  }

  renderTableRows(libraries) {
    return libraries.map(library => <LibraryTableRow key={library.id} {...{library}} />);
  }

  render() {
    const {libraries} = this.props;

    if (libraries.length === 0) {
      return <LibraryTable.EmptyTable />;
    }

    return this.renderTable(this.sorter.sortLibraries(libraries));
  }
}

export class LibrarySorter {
  constructor(sortColumn, sortDirection) {
    this.sortColumn = sortColumn;
    this.sortDirection = sortDirection;

    this.compareCallsCount = this.compareCallsCount.bind(this);
    this.compareCreatedBy = this.compareCreatedBy.bind(this);
    this.compareLastModified = this.compareLastModified.bind(this);
    this.compareLibraryName = this.compareLibraryName.bind(this);
    this.compareSharing = this.compareSharing.bind(this);
  }

  setSortColumn(sortColumn) {
    this.sortColumn = sortColumn;
  }

  setSortDirection(sortDirection) {
    this.sortDirection = sortDirection;
  }

  compareLibraryName(a, b) {
    if (this.sortDirection === 'up') {
      return this.sortText(a.name, b.name);
    } else {
      return -this.sortText(a.name, b.name);
    }
  }

  compareCreatedBy(a, b) {
    if (this.sortDirection === 'up') {
      return this.sortText(a.created_by, b.created_by);
    } else {
      return -this.sortText(a.created_by, b.created_by);
    }
  }

  compareCallsCount(a, b) {
    if (this.sortDirection === 'up') {
      return a.calls_count - b.calls_count;
    } else {
      return b.calls_count - a.calls_count;
    }
  }

  compareLastModified(a, b) {
    if (this.sortDirection === 'up') {
      return this.sortDates(a.modified, b.modified);
    } else {
      return -this.sortDates(a.modified, b.modified);
    }
  }

  compareSharing(a, b) {
    if (this.sortDirection === 'up') {
      return this.sortText(a.shared_type, b.shared_type);
    } else {
      return -this.sortText(a.shared_type, b.shared_type);
    }
  }

  sortText(a, b) {
    if (a < b) {
      return -1;
    }

    if (a > b) {
      return 1;
    }

    return 0;
  }

  sortDates(a, b) {
    if (!a && !b) {
      return 0;
    }

    if (!a) {
      return -1;
    }

    if (!b) {
      return 1;
    }

    const momentA = moment(a);
    const momentB = moment(b);

    if (momentA.isBefore(momentB)) {
      return -1;
    }

    if (momentB.isBefore(momentA)) {
      return 1;
    }

    return 0;
  }

  sortLibraries(libraries) {
    if (this.sortColumn === 'Library Name') {
      return libraries.sort(this.compareLibraryName);
    }

    if (this.sortColumn === 'Created By') {
      return libraries.sort(this.compareCreatedBy);
    }

    if (this.sortColumn === '# Calls') {
      return libraries.sort(this.compareCallsCount);
    }

    if (this.sortColumn === 'Last Modified') {
      return libraries.sort(this.compareLastModified);
    }

    if (this.sortColumn === 'Sharing') {
      return libraries.sort(this.compareSharing);
    }

    return libraries;
  }
}

LibraryTable.EmptyTable = () => (
  <div className="library-message">
    <div className="text">No Libraries Found</div>
  </div>
);

LibraryTable.propTypes = {
  libraries: PropTypes.arrayOf(libraryShape).isRequired,
};

LibraryTable.displayName = 'LibraryTable';
